import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { clearRoutes, setLoading, setHeaderLoading } from "../../store/slices/travelSlice";
import { ArrowLeftRight, Train, Plane, Bus, Plus, Minus, LayoutGrid } from 'lucide-react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import TravelAutocompleteInput from "../search-form/TravelAutocompleteInput";

const FormSearchSection = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [aiSearchInput, setAiSearchInput] = useState("");
    const [activeModes, setActiveModes] = useState(['All']);
    const [showTravelersModal, setShowTravelersModal] = useState(false);
    const [passengerCounts, setPassengerCounts] = useState({
        adults: 1,
        children: 0,
        infants: 0
    });
    const [seatClasses, setSeatClasses] = useState(['Economy', '3A']);
    const [travelDate, setTravelDate] = useState(null);
    const [fromLocation, setFromLocation] = useState("");
    const [toLocation, setToLocation] = useState("");
    const [fromLocationData, setFromLocationData] = useState(null);
    const [toLocationData, setToLocationData] = useState(null);
    const [nonStopOnly, setNonStopOnly] = useState(false);

    const travelersRef = React.useRef(null);

    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (travelersRef.current && !travelersRef.current.contains(event.target)) {
                setShowTravelersModal(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Toggle Mode Logic
    const toggleMode = (mode) => {
        if (mode === 'All') {
            setActiveModes(['All']);
            return;
        }

        setActiveModes(prev => {
            let newModes = [...prev];

            // If currently All is selected, clear it and start with the new mode
            if (newModes.includes('All')) {
                newModes = [];
            }

            if (newModes.includes(mode)) {
                // Deselect logic: prevent deselecting the last mode (optional, but good UX)
                // or replicate "allow empty" which defaults to something? 
                // User requirement: "all else can be selected independently"
                newModes = newModes.filter(m => m !== mode);
            } else {
                newModes.push(mode);
            }

            // If we ended up empty, default back to 'All' or just leave it?
            // "only ALL when selected no other can be left selected"
            // Let's enforce at least one selection? Or default to All if empty.
            if (newModes.length === 0) return ['All'];

            return newModes;
        });
    };

    // Toggle seat class selection (multi-select)
    const toggleSeatClass = (cls) => {
        setSeatClasses(prev => {
            const current = Array.isArray(prev) ? prev : [];
            const isTrainClass = ['1A', '2A', '3A', 'SL'].includes(cls);
            const isFlightClass = ['Economy', 'Business', 'First'].includes(cls);

            if (isTrainClass) {
                const withoutTrain = current.filter(c => !['1A', '2A', '3A', 'SL'].includes(c));
                return [...withoutTrain, cls];
            }
            if (isFlightClass) {
                const withoutFlight = current.filter(c => !['Economy', 'Business', 'First'].includes(c));
                return [...withoutFlight, cls];
            }
            return current;
        });
    };

    const isFormValid = fromLocation && toLocation && travelDate;

    const handleAISearch = (e) => {
        e.preventDefault();
        navigate("/chat", { state: { fromHero: true, heroInput: aiSearchInput } });
    };

    return (
        <section className="home-search-section">
            {/* AI Search Bar - Always Visible */}
            <div className="search-bar" style={{ margin: 0 }}>
                <form onSubmit={handleAISearch} style={{ display: "flex", width: "100%", alignItems: "center" }}>
                    <div className="search-input" style={{ flex: 1 }}>
                        <img src="/Star1.svg" alt="AI" />
                        <input
                            type="text"
                            value={aiSearchInput}
                            onChange={e => setAiSearchInput(e.target.value)}
                            placeholder="Darbhanga se Mumbai jane ke liye kal ki date ke liye routes batao"
                            style={{
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                width: '100%'
                            }}
                        />
                    </div>
                    <button className="search-button" type="submit">
                        <img src="/send.svg" alt="Send" />
                    </button>
                </form>
            </div>

            {/* Divider */}
            <div className="form-divider-or">
                <div className="divider-line"></div>
                OR
                <div className="divider-line"></div>
            </div>

            {/* Advanced Form Search */}
            <div className="form-search-container">
                <div className="home-search-bar-wrapper" style={{ width: '100%', position: 'relative' }}>
                    <div className="home-search-bar">
                        <div className="search-field home-search-main-field">
                            <TravelAutocompleteInput
                                className="search-field-input"
                                placeholder="From"
                                value={fromLocation}
                                onChange={setFromLocation}
                                onSelect={(item) => {
                                    setFromLocation(item.label);
                                    setFromLocationData(item);
                                }}
                            />
                            <div className="swap-btn" onClick={() => {
                                const temp = fromLocation;
                                const tempData = fromLocationData;
                                setFromLocation(toLocation);
                                setFromLocationData(toLocationData);
                                setToLocation(temp);
                                setToLocationData(tempData);
                            }}>
                                <ArrowLeftRight size={20} />
                            </div>
                            <TravelAutocompleteInput
                                className="search-field-input"
                                placeholder="To"
                                value={toLocation}
                                onChange={setToLocation}
                                onSelect={(item) => {
                                    setToLocation(item.label);
                                    setToLocationData(item);
                                }}
                            />
                        </div>

                        <div className="search-field-sep"></div>

                        <div className="search-field date-picker-container">
                            <DatePicker
                                selected={travelDate}
                                onChange={(date) => setTravelDate(date)}
                                dateFormat="MMM dd/eee"
                                className="search-field-input"
                                placeholderText="Select date"
                                minDate={new Date()}
                                isClearable={false}
                            />
                        </div>

                        <div className="search-field-sep"></div>

                        <div
                            className="search-field"
                            ref={travelersRef}
                            onClick={() => setShowTravelersModal(!showTravelersModal)}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className={`search-field-input ${!travelDate || !fromLocation || !toLocation ? 'placeholder' : ''}`}>
                                {(passengerCounts.adults > 0 && travelDate && fromLocation && toLocation) ? `${passengerCounts.adults} adult, ${seatClasses.filter(c => {
                                    if (['1A', '2A', '3A', 'SL'].includes(c)) return activeModes.includes('Train') || activeModes.includes('All');
                                    if (['Economy', 'Business', 'First'].includes(c)) return activeModes.includes('Flight') || activeModes.includes('All');
                                    return true;
                                }).join(', ')}` : "Travelers & Seat classes"}
                            </div>

                            {showTravelersModal && (
                                <div className="travelers-dropdown" onClick={(e) => e.stopPropagation()}>
                                    <h2 className="modal-title">Travelers</h2>
                                    {[
                                        { key: 'adults', label: 'Adults', age: '18+' },
                                        { key: 'children', label: 'Children', age: '2-11' },
                                        { key: 'infants', label: 'Infants', age: 'under 2' }
                                    ].map(({ key, label, age }) => (
                                        <div className="traveler-type-row" key={key}>
                                            <div className="type-info">
                                                <div className="label-with-age">
                                                    <h4>{label}</h4>
                                                    <span>{age}</span>
                                                </div>
                                            </div>
                                            <div className="counter-btns">
                                                <button
                                                    className="count-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setPassengerCounts(prev => ({ ...prev, [key]: Math.max(0, (prev[key] || 0) - 1) }));
                                                    }}
                                                >
                                                    <Minus size={14} />
                                                </button>
                                                <span>{passengerCounts[key] || 0}</span>
                                                <button
                                                    className="count-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setPassengerCounts(prev => ({ ...prev, [key]: (prev[key] || 0) + 1 }));
                                                    }}
                                                >
                                                    <Plus size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}

                                    {/* Seat Classes Section */}
                                    {(activeModes.includes('Flight') || activeModes.includes('All')) && (
                                        <div className="seat-classes-section">
                                            <h2 className="modal-title" style={{ fontSize: '20px', marginTop: '20px' }}>
                                                Flight Seat Classes
                                            </h2>
                                            <div className="seat-classes-grid">
                                                {['Economy', 'Business', 'First'].map(cls => (
                                                    <div
                                                        key={cls}
                                                        className={`class-tag ${seatClasses.includes(cls) ? 'active' : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleSeatClass(cls);
                                                        }}
                                                    >
                                                        {cls}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {(activeModes.includes('Train') || activeModes.includes('All')) && (
                                        <div className="seat-classes-section">
                                            <h2 className="modal-title" style={{ fontSize: '20px', marginTop: '20px' }}>
                                                Train Seat Classes
                                            </h2>
                                            <div className="seat-classes-grid">
                                                {['1A', '2A', '3A', 'SL'].map(cls => (
                                                    <div
                                                        key={cls}
                                                        className={`class-tag ${seatClasses.includes(cls) ? 'active' : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleSeatClass(cls);
                                                        }}
                                                    >
                                                        {cls}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="modal-actions">
                                        <button className="modal-clear-btn" onClick={(e) => {
                                            e.stopPropagation();
                                            setPassengerCounts({ adults: 1, children: 0, infants: 0 });
                                            // Reset seat classes based on current modes
                                            const defaults = [];
                                            if (activeModes.includes('Train') || activeModes.includes('All')) defaults.push('3A');
                                            if (activeModes.includes('Flight') || activeModes.includes('All')) defaults.push('Economy');
                                            setSeatClasses(defaults.length > 0 ? defaults : ['Economy', '3A']);
                                        }}>Clear</button>
                                        <button className="apply-btn" onClick={(e) => {
                                            e.stopPropagation();
                                            setShowTravelersModal(false);
                                        }}>Apply</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="modes-container">
                    <span>Preferred modes</span>
                    <div className="modes-tabs">
                        <button className={`mode-btn ${activeModes.includes('All') ? 'active' : ''}`} onClick={() => toggleMode('All')}>
                            <LayoutGrid size={20} /> All
                        </button>
                        <button className={`mode-btn ${activeModes.includes('Train') ? 'active' : ''}`} onClick={() => toggleMode('Train')}>
                            <Train size={20} /> Train
                        </button>
                        <button className={`mode-btn ${activeModes.includes('Flight') ? 'active' : ''}`} onClick={() => toggleMode('Flight')}>
                            <Plane size={20} /> Flight
                        </button>
                        {/* <button className={`mode-btn ${activeModes.includes('Bus') ? 'active' : ''}`} onClick={() => toggleMode('Bus')}>
                            <Bus size={20} /> Bus
                        </button> */}
                    </div>

                    <div className="options-row">
                        <label className="non-stop">
                            <input
                                type="checkbox"
                                checked={nonStopOnly}
                                onChange={(e) => setNonStopOnly(e.target.checked)}
                            />
                            <span>Non stop only</span>
                        </label>
                    </div>
                    <button
                        className="primary-search-btn"
                        disabled={!isFormValid}
                        onClick={() => {
                            dispatch(clearRoutes());
                            dispatch(setLoading(true));
                            dispatch(setHeaderLoading(true));

                            const params = new URLSearchParams({
                                from: fromLocation,
                                to: toLocation,
                                date: travelDate ? (() => {
                                    const d = new Date(travelDate);
                                    const year = d.getFullYear();
                                    const month = String(d.getMonth() + 1).padStart(2, '0');
                                    const day = String(d.getDate()).padStart(2, '0');
                                    return `${year}-${month}-${day}`;
                                })() : '',
                                adults: passengerCounts.adults,
                                children: passengerCounts.children,
                                infants: passengerCounts.infants,
                                seatClasses: seatClasses.join(','),
                                modes: activeModes.join(','),
                                nonstop: nonStopOnly ? '1' : '0'
                            });
                            navigate(`/search-form?${params.toString()}`, {
                                state: {
                                    fromLocation,
                                    toLocation,
                                    fromLocationData,
                                    toLocationData,
                                    travelDate: travelDate ? travelDate.toISOString() : null,
                                    passengerCounts,
                                    seatClasses,
                                    activeModes,
                                    nonStopOnly: nonStopOnly
                                }
                            });
                        }}
                    >
                        Search Routes
                    </button>
                </div>
            </div>

        </section>
    );
};

export default FormSearchSection;
